import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import produce from 'immer';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconDrag } from '@components/icons/drag';
import { IconEdit } from '@components/icons/edit';
import { IconRemove } from '@components/icons/remove';
import { IconReturn } from '@components/icons/return';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useIsMobile } from '@hooks/use-is-mobile';
import { theme } from '@styles/theme';

import { useStore } from 'models/Provider';
import { EditingTasklist, EditingTasklistItem, TasklistTemplate } from 'types/templates';

export function TasklistForm(props: {
  selected: TasklistTemplate;
  onSave: () => void;
  setEditing: Dispatch<SetStateAction<EditingTasklist>>;
  editing: EditingTasklist;
}) {
  const { onSave, editing, setEditing } = props;
  const [dragging, setDragging] = useState<EditingTasklistItem | null>(null);

  const { t } = useStore();
  const mobile = useIsMobile();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      setDragging(editing.items.find((item) => item.id === Number(event.active.id))!);
    },
    [editing.items]
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!event.active.data.current || !event.over?.data.current) return;

      const from = event.active.data.current.sortable.index;
      const to = event.over.data.current.sortable.index;

      if (from === to) return;

      setEditing((old) => {
        let newItems = [...old.items];
        const itemToMove = newItems.splice(from, 1)[0];
        newItems.splice(to, 0, itemToMove);
        newItems = newItems.map((item, index) => ({
          ...item,
          order: index,
        }));

        return {
          ...old,
          items: newItems,
        };
      });
    },
    [setEditing]
  );

  const addNewTask = useCallback(() => {
    setEditing((old) => ({
      ...old,
      items: [
        ...old.items,
        {
          id: Date.now(),
          order: old.items.length,
          text: '',
          description: '',
          isNew: true,
        },
      ],
    }));
  }, [setEditing]);

  const removeTask = useCallback(
    (taskId: number) => {
      setEditing((old) => ({
        ...old,
        items: old.items
          .filter((task) => task.id !== taskId)
          .map((task, index) => ({
            ...task,
            order: index,
          })),
      }));
    },
    [setEditing]
  );

  const editTaskName = useCallback(
    (id: number, value: string) => {
      setEditing(
        produce((draft) => {
          const item = draft.items.find((item) => item.id === id);
          if (item) {
            item.text = value;
          }
        })
      );
    },
    [setEditing]
  );

  const itemIds = editing.items.map((item) => String(item.id));

  const hasEmptyTasks = editing.items.some((task) => !task.text);

  return (
    <FormContainer>
      <Stack space={6}>
        <Stack space={4}>
          <TextField
            placeholder={t('templates.store.newtasklist.name')}
            label={t('company.templates.tasks.label.title')}
            value={editing.name}
            onChange={(newName) => setEditing((old) => ({ ...old, name: newName }))}
          />
          <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <DragOverlay>{dragging && <TaskOverlay task={dragging} />}</DragOverlay>
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
              <Stack space={1}>
                {editing.items.map((item, index) => (
                  <Task
                    islastTask={index === editing.items.length - 1}
                    addNewTask={addNewTask}
                    onSave={onSave}
                    task={item}
                    key={item.id}
                    onChange={(val) => editTaskName(item.id, val)}
                    onRemove={() => removeTask(item.id)}
                  />
                ))}
              </Stack>
              <Stack align="flex-end">
                <Button
                  isDisabled={hasEmptyTasks}
                  variant="secondary"
                  onClick={addNewTask}
                  iconLeft={<IconAddPlus />}
                  text={t('company.templates.tasks.create')}
                  {...(mobile && { style: { width: '100%' } })}
                />
              </Stack>
            </SortableContext>
          </DndContext>
        </Stack>
      </Stack>
    </FormContainer>
  );
}

function TaskOverlay(props: { task: EditingTasklistItem }) {
  const { task } = props;
  return (
    <TaskCard space={3} variant="default" data-dragging>
      <Stack space={3} orientation="horizontal" justify="space-between">
        <Stack space={3} orientation="horizontal">
          <IconButton icon={<IconDrag />} variant="ghost-purple" />
          <Stack justify="center">
            <Text font="bold14" text={task.text} />
          </Stack>
        </Stack>
        <Stack space={2} orientation="horizontal">
          <IconButton icon={<IconEdit />} variant="ghost-purple" />
          <IconButton icon={<IconRemove />} variant="ghost-purple" />
        </Stack>
      </Stack>
    </TaskCard>
  );
}

function Task(props: {
  islastTask: boolean;
  addNewTask: () => void;
  task: EditingTasklistItem;
  onChange(newValue: string): void;
  onRemove: () => void;
  onSave: () => void;
}) {
  const { t } = useStore();
  const { task, onChange, onRemove, addNewTask, islastTask } = props;
  const [showEditor, setShowEditor] = useState(Boolean(task.isNew));
  const [originalText, setOriginalText] = useState(task.text);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(task.id),
  });
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const isEmpty = task.text && task.text.trim().length > 0 ? false : true;
  const isTaskDirty = originalText === task.text ? false : true;
  const isNew = originalText === '';

  const cancel = () => {
    if (isNew) {
      onRemove();
    } else {
      onChange(originalText);
    }
    setShowEditor(false);
  };

  const save = () => {
    if (isTaskDirty && islastTask && !isEmpty) {
      setOriginalText(task.text);
      addNewTask();
    } else if (isTaskDirty && !isEmpty) {
      setOriginalText(task.text);
    } else if (isNew && isEmpty) {
      onRemove();
    } else if (!isNew && isEmpty) {
      onChange(originalText);
    }
    setShowEditor(false);
  };

  return (
    <div
      style={{
        opacity: isDragging ? 0.2 : 1,
        zIndex: isDragging ? 100 : 0,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      ref={setNodeRef}
    >
      <TaskCard
        variant={showEditor ? 'gray' : 'default'}
        space={3}
        data-dragging={isDragging}
        data-focused={showEditor}
      >
        {showEditor && (
          <Stack space={2} orientation="horizontal">
            <Stack style={{ width: '100%' }}>
              <TextField
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    save();
                  }
                }}
                onBlur={(e) => {
                  if (
                    e.relatedTarget !== addButtonRef.current &&
                    e.relatedTarget !== cancelButtonRef.current
                  ) {
                    save();
                  }
                }}
                autoFocus
                placeholder={t('company.templates.tasks.label.title.placeholder')}
                label={t('company.templates.tasks.label')}
                hideLabel
                type="text"
                value={task.text}
                onChange={(value) => {
                  onChange(value);
                }}
              />
            </Stack>
            <Stack space={2} orientation="horizontal">
              <Button
                ref={cancelButtonRef}
                onClick={() => cancel()}
                text={t('company.templates.button.cancel.task')}
                variant="secondary"
              />
              <Button
                ref={addButtonRef}
                isDisabled={isEmpty || !isTaskDirty}
                onClick={() => save()}
                variant="primary-blue"
                text={
                  originalText
                    ? t('company.templates.button.update.task')
                    : t('company.templates.button.add.task')
                }
                iconLeft={<IconReturn />}
              />
            </Stack>
          </Stack>
        )}
        {!showEditor && (
          <Stack space={3} orientation="horizontal" justify="space-between">
            <Stack space={3} orientation="horizontal">
              <IconButton
                {...attributes}
                {...listeners}
                icon={<IconDrag />}
                variant="ghost-purple"
                style={{ cursor: 'pointer' }}
              />
              <Stack justify="center">
                <Text font="bold14" text={task.text} />
              </Stack>
            </Stack>
            <Stack space={2} orientation="horizontal" justify="space-between">
              <IconButton
                {...attributes}
                {...listeners}
                icon={<IconEdit />}
                variant="ghost-purple"
                onClick={() => {
                  setShowEditor(true);
                }}
              />
              <IconButton
                icon={<IconRemove />}
                variant="ghost-purple"
                onClick={() => {
                  onRemove();
                }}
              />
            </Stack>
          </Stack>
        )}
      </TaskCard>
    </div>
  );
}

const FormContainer = styled.div({
  maxWidth: 580,
  width: '100%',
  margin: 'auto',
});

const TaskCard = styled(Card)({
  '&[data-dragging="true"]': {
    borderColor: theme.color.purple40,
    boxShadow: theme.shadow.high,
  },

  '[data-options="true"]': {
    transition: 'opacity 150ms ease-out',
    opacity: 0,
  },

  '&[data-focused="true"]': {
    borderColor: theme.color.blue100,
  },
});
